<template>
  <div class="row rounded mx-0"
       :id="`mediaContent_${metadata.progId}`">
    <img
      v-if="isLocked || !playoutContent || !playerActive"
      class="col-12 poster poster-rounded"
      :src="posterImage || metadata.backdropSmall"
      @click="onPosterClick"
      alt="poster">
    <Player
      v-if="!isLocked && playoutContent && isOnLine"
      v-show="playerActive"
      class="p-0 col-12"
      :id="`player_${metadata.progId}`"
      :progId="metadata.progId"
      :playoutContent="playoutContent"
      :active="playerActive"
      @error="onPlayerError"
      @ended="onPlayerEnded"
      @fullscreenchanged="onFullScreenChanged"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { SharedFunctions } from '../js/SharedFunctions'
import Player from '../components/video/VideoJS.vue'
import { spinnakerApi } from '../js/SpinnakerApi'

export default {
  page: 'mediaContent',
  components: {
    Player
  },
  props: {
    posterImage: String,
    programmeId: String,
    programmeMetadata: Object,
    keepInitiated: Boolean,
    isLocked: Boolean,
    isVisible: Boolean
  },
  data: function () {
    return {
      metadata: { },
      isOnLine: false,
      playerActive: false,
      playoutContent: null,
      isFullScreen: false
    }
  },
  computed: {
    ...mapState({
      isScrollOngoing: state => state.navigation.isScrollOngoing
    })
  },
  watch: {
    isVisible () {
      this.setupPlayer()
    },
    isScrollOngoing () {
      this.setupPlayer()
    }
  },
  created () {
    this.storeUnsubscribe = this.$store.subscribe((mutation, state) => {
      switch (mutation.type) {
        // pause the content and show poster again on player change
        case 'playout/startPlayout':
          if (this.playerActive && state.playout.progId !== this.metadata.progId) {
            console.info(`MediaContent: Deactivating ${this.metadata.title}`)
            this.playerActive = false
          }
          break
        case 'navigation/goToCategory':
          if (this.playerActive) {
            console.info(`MediaContent: Deactivating ${this.metadata.title}`)
            this.playerActive = false
          }
          break
      }
    })

    this.isOnLine = navigator.onLine
    window.addEventListener('online', this.onOnLineStatusChange)

    this.prepareMetadata().then(result => {
      this.metadata = result
      this.setupPlayer()
    })
  },
  beforeDestroy () {
    this.storeUnsubscribe()
    window.removeEventListener('online', this.onOnLineStatusChange)
  },
  methods: {
    async prepareMetadata () {
      let metadata = this.programmeMetadata
      if (!metadata && this.programmeId) {
        metadata = await spinnakerApi.getProgrammeDetailsWithMapping(this.programmeId)
      }
      return metadata || {}
    },

    setupPlayer () {
      if (this.isScrollOngoing || SharedFunctions.isInLandscapeMode() || this.isFullscreen) {
        return
      }
      if (this.isVisible) {
        this.initPlayer()
      } else {
        if (this.keepInitiated) {
          // in promo mode (on carousel) assets are not visible, but should be always initiated
          // otherwise we end up with double tap to start playback
          // but when asset is not visible and was playing, than playback has to be stopped
          if (this.playerActive) {
            this.tearDownPlayer()
          }
          this.initPlayer()
        } else {
          this.tearDownPlayer()
        }
      }
    },

    initPlayer () {
      if (!this.playoutContent && this.metadata.progId) {
        console.info(`MediaContent.initPlayer: Initiating player ${this.metadata.title}`)
        this.playoutContent = {
          type: this.metadata.youtubeLink ? 'video/youtube' : 'application/x-mpegurl',
          progId: this.metadata.progId,
          manifestUrl: this.metadata.youtubeLink ? this.metadata.youtubeLink : this.metadata.manifestUrl,
          title: this.metadata.title,
          poster: this.metadata.backdropSmall,
          ads: this.metadata.ads,
          useRedeem: this.metadata.useRedeem,
          dataSetup: this.metadata.youtubeLink
            ? '{ "techOrder": [ "youtube"], "youtube": { "enablePrivacyEnhancedMode": "true" } }'
            : '{ "techOrder": ["html5"] }'
        }
      }
    },

    tearDownPlayer () {
      if (this.playoutContent) {
        console.info(`MediaContent.tearDownPlayer: Deactivating player ${this.metadata.title}`)
        // display poster and hide the player
        this.playerActive = false
        // disposes the player once item not visible
        this.playoutContent = null
      }
    },

    showPlayerError () {
      this.$store.commit('showDialogue', {
        title: 'Playback error!',
        subtitle: 'Please check network connection and try again.'
      })
    },

    onPosterClick () {
      this.$emit('contentClicked')

      const canBePlayed = !!this.metadata.progId
      if (!canBePlayed) {
        console.info(`MediaContent.onPosterClick: No playback metadata`)
        return
      }
      if (this.isLocked) {
        console.info(`MediaContent.onPosterClick: Playback is locked`)
        return
      }
      if (this.metadata.externalLink) {
        console.info(`MediaContent.onPosterClick: Opening external link ${this.metadata.title}`)
        window.open(this.metadata.externalLink, '_blank')
        return
      }

      console.info(`MediaContent.onPosterClick: Activating ${this.metadata.title}`)
      this.initPlayer()

      if (!this.isOnLine || !navigator.onLine) {
        this.showPlayerError()
        return
      }

      this.$store.commit('playout/startPlayout', this.metadata.progId)
      this.playerActive = true
    },

    onPlayerError () {
      console.info(`MediaContent.onPlayerError`)
      if (this.playerActive) {
        // Don't show errors when pre-caching etc.
        this.showPlayerError()
        this.playerActive = false
      }
    },

    onPlayerEnded () {
      console.info(`MediaContent.onPlayerEnded`)
      this.playerActive = false
    },

    onFullScreenChanged (fullScreenEnabled) {
      console.info(`MediaContent.handleFullScreenChanged: ${fullScreenEnabled}`)
      this.isFullScreen = fullScreenEnabled
    },

    onOnLineStatusChange () {
      if (navigator.onLine) {
        console.info(`MediaContent.onOnLineStatusChange: Just gone online ${this.metadata.title}`)
        this.isOnLine = true
        window.removeEventListener('online', this.onOnLineStatusChange)
      }
    }
  }
}
</script>
